import { Fragment, useCallback, useMemo, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Drawer,
  Separator,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@admin-panel/ui';

import {
  StylingWorkflowDTOType,
  StylingWorkflowListItemDTOType,
  LookDTOType,
} from '../../../schemas/styling';
import { useGetLooksInfiniteQuery } from '../../../store/stylist/stylistApi';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import WorkflowRunItem from './WorkflowRunItem';

interface StylingWorkflowDetailDrawerProps {
  workflow: StylingWorkflowDTOType | StylingWorkflowListItemDTOType | null;
  onClose: () => void;
}

interface WorkflowRunStateViewProps {
  looksError: unknown;
  isLoadingLooks: boolean;
  isFetchingLooks: boolean;
  hasCurrentLooksData: boolean;
  looks: Array<LookDTOType>;
  emptyLabel: string;
  canReject: boolean;
  canEdit: boolean;
  isFetchingNextPage: boolean;
  dialogPortalContainer?: HTMLElement | null;
  lastItemRef?: (node: HTMLElement | null) => void;
}

const WorkflowRunStateView = ({
  looksError,
  isLoadingLooks,
  isFetchingLooks,
  hasCurrentLooksData,
  looks,
  emptyLabel,
  canReject,
  canEdit,
  isFetchingNextPage,
  dialogPortalContainer,
  lastItemRef,
}: WorkflowRunStateViewProps) => {
  if (looksError) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <div className="font-medium">Error loading styling looks</div>
        <div className="mt-1 text-rose-600">Please try again later.</div>
      </div>
    );
  }

  if (isLoadingLooks || (isFetchingLooks && !hasCurrentLooksData && looks.length === 0)) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (looks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 snap-y snap-proximity space-y-3 overflow-y-auto overscroll-none">
      {looks.map((look, index) => (
        <Fragment key={look.id}>
          <WorkflowRunItem
            run={look}
            canApprove={true}
            canReject={canReject}
            canEdit={canEdit}
            dialogPortalContainer={dialogPortalContainer}
            lastItemRef={index === looks.length - 1 ? lastItemRef : undefined}
          />
          {index !== looks.length - 1 && <Separator />}
        </Fragment>
      ))}
      {isFetchingNextPage ? <Skeleton className="h-20 w-full" /> : null}
    </div>
  );
};

const StylingWorkflowDetailDrawer = ({ workflow, onClose }: StylingWorkflowDetailDrawerProps) => {
  const [activeTab, setActiveTab] = useState<string>('pending');
  const [dialogPortalContainer, setDialogPortalContainer] = useState<HTMLElement | null>(null);
  const workflowId = workflow?.id;
  const setDrawerContentRef = useCallback((node: HTMLDivElement | null) => {
    setDialogPortalContainer(prev => (prev === node ? prev : node));
  }, []);

  const {
    data: looksData,
    currentData: currentLooksData,
    isLoading: isLoadingLooks,
    isFetching: isFetchingLooks,
    error: looksError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetLooksInfiniteQuery(
    {
      workflow_id: workflowId,
      filters: [
        {
          field: 'qc_status',
          filterType: 'exact',
          value:
            activeTab === 'pending'
              ? 'pending'
              : activeTab === 'rejected'
                ? 'rejected'
                : 'published',
        },
      ],
    },
    {
      skip: !workflowId,
    },
  );

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const looks = useMemo(() => {
    const dataToRender = currentLooksData ?? looksData;

    if (!dataToRender?.pages) {
      return [];
    }
    return dataToRender.pages.flatMap(page => {
      if (!page) return [];
      // Handle new format (direct array)
      if (Array.isArray(page)) {
        return page;
      }
      // Handle old format (paginated with results)
      return page?.results || [];
    });
  }, [currentLooksData, looksData]);

  return (
    <Drawer.Root
      open={Boolean(workflow)}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose();
        }
      }}
      direction="right"
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[1000] bg-black/35 backdrop-blur-sm data-[state=closed]:opacity-0" />
        <Drawer.Content
          ref={setDrawerContentRef}
          className="fixed inset-y-0 right-0 z-[1000] flex h-full w-full flex-col bg-background shadow-2xl outline-none data-[state=closed]:pointer-events-none"
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
            {!workflow ? (
              <>
                <header className="flex items-start justify-between border-b border-border px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-foreground">
                      Workflow details unavailable
                    </div>
                  </div>
                  <Drawer.Close asChild>
                    <Button
                      type="button"
                      size="small"
                      variant="ghost"
                      aria-label="Close workflow details"
                      icon={<XIcon size={16} />}
                    />
                  </Drawer.Close>
                </header>
                <div className="flex h-full items-center justify-center p-8">
                  <div className="space-y-3 text-center">
                    <p className="text-sm text-muted-foreground">No workflow selected.</p>
                    <Button size="small" variant="outline" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <Tabs
                key={workflow.id}
                value={activeTab}
                onValueChange={setActiveTab}
                className="flex h-full min-h-0 w-full flex-col"
              >
                <header className="flex items-start justify-between gap-3 border-b border-border px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-foreground">{workflow.name}</div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <TabsList>
                      <TabsTrigger value="pending">
                        QC Pending
                        {activeTab === 'pending' ? (
                          <Badge tone="warning">{looks.length}</Badge>
                        ) : null}
                      </TabsTrigger>
                      <TabsTrigger value="published">
                        Published
                        {activeTab === 'published' ? (
                          <Badge tone="info">{looks.length}</Badge>
                        ) : null}
                      </TabsTrigger>
                      <TabsTrigger value="rejected">
                        QC Rejected
                        {activeTab === 'rejected' ? (
                          <Badge tone="destructive">{looks.length}</Badge>
                        ) : null}
                      </TabsTrigger>
                    </TabsList>
                    <Drawer.Close asChild>
                      <Button
                        type="button"
                        size="small"
                        variant="ghost"
                        aria-label="Close workflow details"
                        icon={<XIcon size={20} />}
                        iconPosition="end"
                      >
                        Close
                      </Button>
                    </Drawer.Close>
                  </div>
                </header>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3">
                  <TabsContent value="pending" className="h-full min-h-0">
                    <WorkflowRunStateView
                      looksError={looksError}
                      isLoadingLooks={isLoadingLooks}
                      isFetchingLooks={isFetchingLooks}
                      hasCurrentLooksData={Boolean(currentLooksData)}
                      looks={looks}
                      emptyLabel="No pending looks found"
                      canReject={true}
                      canEdit={true}
                      isFetchingNextPage={isFetchingNextPage}
                      dialogPortalContainer={dialogPortalContainer}
                      lastItemRef={lastItemRef}
                    />
                  </TabsContent>
                  <TabsContent value="published" className="h-full min-h-0">
                    <WorkflowRunStateView
                      looksError={looksError}
                      isLoadingLooks={isLoadingLooks}
                      isFetchingLooks={isFetchingLooks}
                      hasCurrentLooksData={Boolean(currentLooksData)}
                      looks={looks}
                      emptyLabel="No published looks found"
                      canReject={false}
                      canEdit={true}
                      isFetchingNextPage={isFetchingNextPage}
                      dialogPortalContainer={dialogPortalContainer}
                      lastItemRef={lastItemRef}
                    />
                  </TabsContent>
                  <TabsContent value="rejected" className="h-full min-h-0">
                    <WorkflowRunStateView
                      looksError={looksError}
                      isLoadingLooks={isLoadingLooks}
                      isFetchingLooks={isFetchingLooks}
                      hasCurrentLooksData={Boolean(currentLooksData)}
                      looks={looks}
                      emptyLabel="No rejected looks found"
                      canReject={false}
                      canEdit={false}
                      isFetchingNextPage={isFetchingNextPage}
                      dialogPortalContainer={dialogPortalContainer}
                      lastItemRef={lastItemRef}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default StylingWorkflowDetailDrawer;
